import React, { useState } from 'react';
import {
    ChevronLeft, ChevronRight, Mail, Phone, MessageSquare,
    CheckCircle
} from 'lucide-react';
import CustomSelect from './CustomSelect';
import EmailPreview from './EmailPreview';
import AdvancedContactSelector from './AdvancedContactSelector';
import { getAuthorizedHeaders } from '../utils/authorizedHeaders';

//const API_BASE = 'http://localhost:3001';
const API_BASE = 'https://warngen-api-352514293915.europe-west1.run.app';

const CreateAlert = ({
    plantillas,
    contactos,
    listas = [],
    fetchContactosDeLista,
    usuario,
    tiposAlerta,
    onVolver,
    registrarActividad,
    aprobacionRequerida,
    setAlertasPendientes,
    alertasPendientes,
    setAlertasProgramadas,
    alertasProgramadas,
    clienteActivo = null,
    clientes = []
}) => {

    // Estado del flujo de alerta
    const [flujoAlerta, setFlujoAlerta] = useState(() => {
        const guardado = sessionStorage.getItem('flujoAlertaGuardado');
        if (guardado) {
            try { return JSON.parse(guardado); } catch (e) {}
        }
        return {
            paso: 1,
            tipoAlerta: null,
            canales: { email: false, sms: false, whatsapp: false },
            plantillasSeleccionadas: { email: null, sms: null, whatsapp: null },
            contactosSeleccionados: [],
            variables: {},
            preview: null,
            emisorEmailId: null,
            emisorSms: '',        // '' = usar la configuración del cliente
            emisorWhatsapp: '',   // '' = usar la configuración del cliente
            programacion: {
                inmediata: true,
                programada: { activa: false, fechaHora: '' },
                recurrente: { activa: false, intervalo: 1, unidad: 'horas', fechaInicio: '', fechaFin: '' }
            }
        };
    });

    // Emisores de email disponibles
    const [emisores, setEmisores] = useState([]);
    const [clientesList, setClientesList] = useState([]);
    const [remitentes, setRemitentes] = useState({ numeros: [], sms_por_defecto: null, whatsapp_por_defecto: null });
    const [smsNombrePersonalizado, setSmsNombrePersonalizado] = useState(false);

    React.useEffect(() => {
        fetch(`${API_BASE}/api/emisores`, { headers: getAuthorizedHeaders() })
            .then(r => r.json()).then(d => { if (d.success) setEmisores(d.data); }).catch(() => {});
        fetch(`${API_BASE}/api/clientes`, { headers: getAuthorizedHeaders() })
            .then(r => r.json()).then(d => { if (d.success) setClientesList(d.data); }).catch(() => {});
        fetch(`${API_BASE}/api/remitentes`, { headers: getAuthorizedHeaders() })
            .then(r => r.json()).then(d => { if (d.success) setRemitentes(d.data); }).catch(() => {});
    }, []);

    // Remitente efectivo según configuración del cliente (para mostrar al usuario)
    const smsClienteDefecto = clienteActivo?.sms_sender_id || clienteActivo?.sms_number || remitentes.sms_por_defecto || 'número del sistema';
    const waClienteDefecto = (clienteActivo?.whatsapp_number || '').replace('whatsapp:', '') || remitentes.whatsapp_por_defecto || 'número del sistema';
    const esNumero = (v) => /^\+?\d{6,}$/.test(String(v || '').replace(/\s/g, ''));


    // Estado selector de listas
    const [cargandoLista, setCargandoLista] = useState(false);
    const [listasAplicadas, setListasAplicadas] = useState(() => {
        const guardado = sessionStorage.getItem('listasAplicadasGuardadas');
        if (guardado) {
            try { return JSON.parse(guardado); } catch (e) {}
        }
        return [];
    });


    // Efecto para guardar estado
    React.useEffect(() => {
        sessionStorage.setItem('flujoAlertaGuardado', JSON.stringify(flujoAlerta));
    }, [flujoAlerta]);

    React.useEffect(() => {
        sessionStorage.setItem('listasAplicadasGuardadas', JSON.stringify(listasAplicadas));
    }, [listasAplicadas]);

    const handleVolver = () => {
        sessionStorage.removeItem('flujoAlertaGuardado');
        sessionStorage.removeItem('listasAplicadasGuardadas');
        onVolver();
    };

    // Toggle canal
    const toggleCanal = (canal) => {
        setFlujoAlerta({
            ...flujoAlerta,
            canales: {
                ...flujoAlerta.canales,
                [canal]: !flujoAlerta.canales[canal]
            }
        });
    };

    // Generar preview
    const generarPreview = () => {
        const previews = {};
        const primerContacto = contactos.find(c => flujoAlerta.contactosSeleccionados.includes(c.id)) || contactos[0] || null;
        const autoValoresContacto = {
            nombre: primerContacto?.nombre || 'María García',
            zona: primerContacto?.zona || primerContacto?.distrito_municipal || 'Distrito 1 — Centro',
            distrito: primerContacto?.zona || primerContacto?.distrito_municipal || 'Distrito 1 — Centro',
            distrito_municipal: primerContacto?.zona || primerContacto?.distrito_municipal || 'Distrito 1 — Centro',
        };

        Object.keys(flujoAlerta.canales).forEach(canal => {
            if (flujoAlerta.canales[canal] && flujoAlerta.plantillasSeleccionadas[canal]) {
                const plantilla = plantillas.find(
                    p => p.id === flujoAlerta.plantillasSeleccionadas[canal]
                );

                if (plantilla) {
                    let mensaje = plantilla.mensaje;
                    let asunto = plantilla.asunto || '';
                    const varsManuales = Array.isArray(plantilla.variables) ? plantilla.variables : JSON.parse(plantilla.variables || '[]');
                    const detectadas = [...(mensaje + asunto).matchAll(/{{(.*?)}}/g)].map(m => m[1].trim());
                    const todasVars = [...new Set([...varsManuales.map(v => v.toString().trim()), ...detectadas])];

                    todasVars.forEach(vnClean => {
                        let val = flujoAlerta.variables?.[vnClean];
                        // Si la variable es numérica (ej. "1", "2"), mirar si está mapeada en varsManuales (ej. ["nombre", "zona"])
                        if (!val && !isNaN(vnClean)) {
                            const mappedName = varsManuales[Number(vnClean) - 1];
                            if (mappedName) {
                                val = flujoAlerta.variables?.[mappedName] || autoValoresContacto[mappedName];
                            }
                        }
                        if (!val && autoValoresContacto[vnClean]) {
                            val = autoValoresContacto[vnClean];
                        }
                        const valMarker = val ? `[[RESALTAR]]${val}[[FIN]]` : `{{${vnClean}}}`;
                        
                        // Reemplazo Directo (Soporta {{ var }} o {{var}})
                        const regexDirect = new RegExp(`{{\\s*${vnClean}\\s*}}`, 'g');
                        mensaje = mensaje.replace(regexDirect, valMarker);
                        asunto = asunto.replace(regexDirect, valMarker);

                        // Soporte Especial WhatsApp (nombre -> {{indice}})
                        if (canal === 'whatsapp') {
                            const idx = varsManuales.findIndex(v => v.toString().trim() === vnClean);
                            if (idx !== -1) {
                                const regexIdx = new RegExp(`{{\\s*${idx + 1}\\s*}}`, 'g');
                                mensaje = mensaje.replace(regexIdx, valMarker);
                            }
                        }
                    });

                    previews[canal] = { asunto, mensaje };
                }
            }
        });

        setFlujoAlerta({ ...flujoAlerta, preview: previews, paso: 6 });
    };

    // Enviar alertas (lógica principal)
    const enviarAlertas = async () => {
        if (flujoAlerta.contactosSeleccionados.length === 0) {
            alert('Seleccione al menos un contacto');
            return;
        }

        if (!window.confirm(`¿Estás seguro de que deseas enviar esta alerta a ${flujoAlerta.contactosSeleccionados.length} destinatarios?`)) {
            return;
        }

        // Verificar si hay programaciones
        const tieneProgramaciones = flujoAlerta.programacion.programada.activa || flujoAlerta.programacion.recurrente.activa;

        // Lógica para alertas programadas
        if (tieneProgramaciones) {
            const contactosEnvio = contactos.filter(c => flujoAlerta.contactosSeleccionados.includes(c.id));
            const plantillasAlerta = {};

            Object.keys(flujoAlerta.canales).forEach(canal => {
                if (flujoAlerta.canales[canal]) {
                    const plantillaId = flujoAlerta.plantillasSeleccionadas[canal];
                    const plantilla = plantillas.find(p => p.id === plantillaId);
                    if (plantilla) {
                        plantillasAlerta[canal] = plantilla;
                    }
                }
            });

            const nuevasAlertasProgramadas = [...alertasProgramadas];

            // Alerta programada única
            if (flujoAlerta.programacion.programada.activa) {
                const proximaEjecucion = new Date(flujoAlerta.programacion.programada.fechaHora).toISOString();
                const alertaProgramada = {
                    id: Date.now(),
                    usuario: usuario.nombre,
                    usuario_id: usuario.id,
                    fecha_creacion: new Date().toISOString(),
                    estado: 'activa',
                    contactos: contactosEnvio,
                    tipo_alerta: flujoAlerta.tipoAlerta,
                    canales: flujoAlerta.canales,
                    plantillas: plantillasAlerta,
                    variables: flujoAlerta.variables,
                    programacion: { tipo: 'programada', ...flujoAlerta.programacion.programada },
                    proximaEjecucion: proximaEjecucion,
                    ejecuciones: 0
                };
                nuevasAlertasProgramadas.push(alertaProgramada);
                registrarActividad(
                    `Programó alerta ${flujoAlerta.tipoAlerta} para ${new Date(proximaEjecucion).toLocaleString()}`,
                    'alertas', 'programar',
                    { alerta_id: alertaProgramada.id, tipo: flujoAlerta.tipoAlerta, tipo_programacion: 'programada' }
                );
            }

            // Alerta recurrente
            if (flujoAlerta.programacion.recurrente.activa) {
                const proximaEjecucion = new Date(flujoAlerta.programacion.recurrente.fechaInicio).toISOString();
                const alertaRecurrente = {
                    id: Date.now() + 1,
                    usuario: usuario.nombre,
                    usuario_id: usuario.id,
                    fecha_creacion: new Date().toISOString(),
                    estado: 'activa',
                    contactos: contactosEnvio,
                    tipo_alerta: flujoAlerta.tipoAlerta,
                    canales: flujoAlerta.canales,
                    plantillas: plantillasAlerta,
                    variables: flujoAlerta.variables,
                    programacion: { tipo: 'recurrente', ...flujoAlerta.programacion.recurrente },
                    proximaEjecucion: proximaEjecucion,
                    ejecuciones: 0
                };
                nuevasAlertasProgramadas.push(alertaRecurrente);
                registrarActividad(
                    `Programó alerta recurrente ${flujoAlerta.tipoAlerta}`,
                    'alertas', 'programar',
                    { alerta_id: alertaRecurrente.id, tipo: flujoAlerta.tipoAlerta, tipo_programacion: 'recurrente' }
                );
            }

            setAlertasProgramadas(nuevasAlertasProgramadas);
            localStorage.setItem('alertasProgramadas', JSON.stringify(nuevasAlertasProgramadas));

            let mensaje = 'Alertas programadas correctamente.\n';
            if (!flujoAlerta.programacion.inmediata) {
                alert(mensaje);
                handleVolver();
                return;
            }
        }

        // Enviar alerta directamente (o como pendiente si el usuario es operador, manejado por el backend)
        try {
            const contactosEnvio = contactos.filter(c => flujoAlerta.contactosSeleccionados.includes(c.id));
            const plantillasEnvio = {};

            Object.keys(flujoAlerta.canales).forEach(canal => {
                if (flujoAlerta.canales[canal]) {
                    const plantillaId = flujoAlerta.plantillasSeleccionadas[canal];
                    const plantilla = plantillas.find(p => p.id === plantillaId);
                    if (plantilla) {
                        const plantillaBilingue = { ...plantilla };
                        plantillaBilingue.mensaje = `[ESPAÑOL / SPANISH]\n${plantilla.mensaje}\n\n[ENGLISH / INGLÉS]\n${plantilla.mensaje_en || plantilla.mensaje}`;
                        if (canal === 'email' && plantilla.asunto) {
                            plantillaBilingue.asunto = `${plantilla.asunto} / ${plantilla.asunto_en || plantilla.asunto}`;
                        }
                        plantillasEnvio[canal] = plantillaBilingue;
                    }
                }
            });

            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/api/enviar/alerta`, {
                method: 'POST',
                /*headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },*/
                headers: getAuthorizedHeaders(),
                body: JSON.stringify({
                    contactos_ids: flujoAlerta.contactosSeleccionados, // Send IDs
                    contactos: contactosEnvio, 
                    tipo_alerta: flujoAlerta.tipoAlerta,
                    canales: flujoAlerta.canales,
                    plantillas_ids: flujoAlerta.plantillasSeleccionadas, // Send Template IDs
                    variables: flujoAlerta.variables,
                    usuario_id: usuario?.id,
                    cliente_id: clienteActivo?.id || null,
                    emisor_sms: flujoAlerta.emisorSms || null,
                    emisor_whatsapp: flujoAlerta.emisorWhatsapp || null,
                    emisor_email_id: flujoAlerta.emisorEmailId || null,
                    nombre_campana: flujoAlerta.nombreCampana || null
                })

            });

            const resultado = await response.json();

            if (resultado.success) {
                if (resultado.pending) {
                    alert(resultado.mensaje);
                    handleVolver();
                    return;
                }

                registrarActividad(
                    `Envió alerta tipo ${flujoAlerta.tipoAlerta} a ${contactosEnvio.length} contactos`,
                    'alertas', 'enviar',
                    { tipo_alerta: flujoAlerta.tipoAlerta, exitosos: resultado.resultados.resumen.exitosos, fallidos: resultado.resultados.resumen.fallidos }
                );

                alert(`Envío completado:\n${resultado.resultados.resumen.exitosos} exitosos\n${resultado.resultados.resumen.fallidos} fallidos`);
                handleVolver();
            } else {
                const errorMsg = resultado.error || resultado.mensaje || 'Error desconocido del servidor';
                alert('Error al enviar alertas: ' + errorMsg);
            }

        } catch (error) {
            console.error('Error al enviar alertas:', error);
            alert('Error al enviar alertas: ' + error.message);
            registrarActividad(`Error al enviar alerta: ${error.message}`, 'alertas', 'error', { error: error.message });
        }
    };

    // Renderizar Pasos
    if (flujoAlerta.paso === 1) {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <button onClick={handleVolver} className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">
                        <ChevronLeft size={24} />
                    </button>
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Crear Nueva Alerta</h2>
                </div>
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Paso 1 de 6: Selecciona el tipo de alerta</h3>
                    <div className="max-w-md">
                        
                        {/* Nombre de la Campana */}
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Nombre de la Comunicación (Opcional)
                            </label>
                            <input
                                type="text"
                                value={flujoAlerta.nombreCampana || ''}
                                onChange={e => setFlujoAlerta({...flujoAlerta, nombreCampana: e.target.value})}
                                placeholder="Ej. Campaña Navidad 2026"
                                className="w-full px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-gray-100"
                            />
                        </div>

                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tipo de Alerta *</label>
                        {(() => {
                            const fallbackTipos = [
                                { codigo: 'DEMO', nombre: 'Alerta DEMO / Pruebas', categoria: 'demo' },
                                { codigo: 'GEN', nombre: 'General / Comunicado', categoria: 'general' },
                                { codigo: 'INUNDACION', nombre: 'Inundación / Lluvias', categoria: 'emergencia' },
                                { codigo: 'INCENDIO', nombre: 'Incendio / Evacuación', categoria: 'emergencia' },
                                { codigo: 'CORTE_AGUA', nombre: 'Corte de Suministro de Agua', categoria: 'servicios' },
                                { codigo: 'CORTE_LUZ', nombre: 'Corte de Suministro Eléctrico', categoria: 'servicios' },
                                { codigo: 'METEOROLOGIA', nombre: 'Alerta Meteorológica', categoria: 'emergencia' },
                                { codigo: 'TRAFICO', nombre: 'Incidencia de Tráfico', categoria: 'servicios' },
                                { codigo: 'SEGURIDAD', nombre: 'Aviso de Seguridad', categoria: 'seguridad' }
                            ];

                            const listaBase = (tiposAlerta && tiposAlerta.length > 0) ? tiposAlerta : fallbackTipos;

                            // Asegurar que DEMO y GEN siempre están presentes
                            const listaTipos = [...listaBase];
                            fallbackTipos.forEach(fb => {
                                if (!listaTipos.some(t => t.codigo === fb.codigo)) {
                                    listaTipos.push(fb);
                                }
                            });

                            const demos = listaTipos.filter(t => t.categoria === 'demo' || t.codigo === 'DEMO' || t.codigo === 'GEN' || t.codigo === 'M20');
                            const codigosDemo = demos.map(d => d.codigo);
                            const otrosTipos = listaTipos.filter(t => !codigosDemo.includes(t.codigo));

                            const otrosPlantillas = [...new Set(plantillas
                                .filter(p => p.tipo_alerta_codigo && !listaTipos.some(t => t.codigo === p.tipo_alerta_codigo))
                                .map(p => p.tipo_alerta_codigo)
                            )];

                            const options = [
                                { value: '', label: '-- Selecciona un tipo de alerta --' }
                            ];

                            if (demos.length > 0) {
                                options.push({ label: 'Alertas Demo y Pruebas', isLabel: true });
                                demos.forEach(tipo => {
                                    options.push({ value: tipo.codigo, label: `${tipo.codigo} — ${tipo.nombre || tipo.codigo}` });
                                });
                            }

                            if (otrosTipos.length > 0 || otrosPlantillas.length > 0) {
                                options.push({ label: 'Tipos de Alerta del Sistema', isLabel: true });
                                otrosTipos.forEach(tipo => {
                                    options.push({ value: tipo.codigo, label: `${tipo.codigo} — ${tipo.nombre || tipo.codigo}` });
                                });
                                otrosPlantillas.forEach(codigo => {
                                    options.push({ value: codigo, label: codigo });
                                });
                            }

                            return (
                                <CustomSelect
                                    value={flujoAlerta.tipoAlerta || ''}
                                    onChange={(v) => setFlujoAlerta({ ...flujoAlerta, tipoAlerta: v })}
                                    options={options}
                                />
                            );
                        })()}

                    </div>
                    <div className="mt-6">
                        <button
                            onClick={() => {
                                if (!flujoAlerta.tipoAlerta) return alert('Selecciona un tipo de alerta');
                                setFlujoAlerta({ ...flujoAlerta, paso: 2 });
                            }}
                            className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition"
                        >
                            Siguiente
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (flujoAlerta.paso === 2) {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <button onClick={() => setFlujoAlerta({ ...flujoAlerta, paso: 1 })} className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">
                        <ChevronLeft size={24} />
                    </button>
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Crear Alerta {flujoAlerta.tipoAlerta}</h2>
                </div>
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Paso 2 de 6: Selecciona los canales de envío</h3>
                    <div className="space-y-3">
                        {[
                            { id: 'email', icon: Mail, label: 'Email', desc: 'Enviar por correo electrónico' },
                            { id: 'sms', icon: Phone, label: 'SMS', desc: 'Enviar por mensaje de texto' },
                            { id: 'whatsapp', icon: MessageSquare, label: 'WhatsApp', desc: 'Enviar por WhatsApp' }
                        ].map(canal => (
                            <label key={canal.id} className="flex items-center gap-3 p-4 border-2 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                                <input
                                    type="checkbox"
                                    checked={flujoAlerta.canales[canal.id]}
                                    onChange={() => toggleCanal(canal.id)}
                                    className="w-5 h-5 text-primary-600"
                                />
                                <canal.icon size={24} className="text-primary-600" />
                                <div className="flex-1">
                                    <div className="font-semibold dark:text-gray-100">{canal.label}</div>
                                    <div className="text-sm text-gray-600 dark:text-gray-400">{canal.desc}</div>
                                </div>
                            </label>
                        ))}
                    </div>
                    <div className="mt-6">
                        <button
                            onClick={() => {
                                if (!Object.values(flujoAlerta.canales).some(v => v)) return alert('Selecciona al menos un canal');
                                setFlujoAlerta({ ...flujoAlerta, paso: 3 });
                            }}
                            className="w-full bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition font-semibold flex items-center justify-center gap-2"
                        >
                            Continuar <ChevronRight size={20} />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Paso 3: Plantillas
    if (flujoAlerta.paso === 3) {
        const canalesActivos = Object.keys(flujoAlerta.canales).filter(c => flujoAlerta.canales[c]);
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <button onClick={() => setFlujoAlerta({ ...flujoAlerta, paso: 2 })} className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">
                        <ChevronLeft size={24} />
                    </button>
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Crear Alerta {flujoAlerta.tipoAlerta}</h2>
                </div>
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Paso 3 de 6: Selecciona las plantillas</h3>

                    {/* Selector de emisor de email */}
                    {flujoAlerta.canales.email && emisores.length > 0 && (
                        <div className="mb-6 p-4 border-2 border-dashed border-blue-200 dark:border-blue-800 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                Emisor del email (campo "De:" que vera el receptor)
                            </label>
                            <select
                                value={flujoAlerta.emisorEmailId || ''}
                                onChange={e => setFlujoAlerta({ ...flujoAlerta, emisorEmailId: e.target.value ? parseInt(e.target.value) : null })}
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 dark:text-gray-100"
                            >
                                <option value="">Por defecto (WarnGen)</option>
                                {emisores.map(em => (
                                    <option key={em.id} value={em.id}>{em.nombre} — {em.email}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Selector de remitente SMS */}
                    {flujoAlerta.canales.sms && (() => {
                        const valorSms = flujoAlerta.emisorSms || '';
                        const modoPersonalizado = smsNombrePersonalizado || (valorSms && !esNumero(valorSms));
                        return (
                            <div className="mb-6 p-4 border-2 border-dashed border-amber-200 dark:border-amber-800 rounded-lg bg-amber-50 dark:bg-amber-900/20">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                    Remitente del SMS
                                </label>
                                <select
                                    value={modoPersonalizado ? '__nombre__' : valorSms}
                                    onChange={e => {
                                        const v = e.target.value;
                                        if (v === '__nombre__') {
                                            setSmsNombrePersonalizado(true);
                                            setFlujoAlerta({ ...flujoAlerta, emisorSms: clienteActivo?.sms_sender_id || 'WarnGen' });
                                        } else {
                                            setSmsNombrePersonalizado(false);
                                            setFlujoAlerta({ ...flujoAlerta, emisorSms: v });
                                        }
                                    }}
                                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 dark:text-gray-100 font-mono"
                                >
                                    <option value="">Configuración del cliente ({smsClienteDefecto})</option>
                                    {remitentes.numeros.filter(n => n.sms).map(n => (
                                        <option key={n.numero} value={n.numero}>{n.numero} — {n.nombre}</option>
                                    ))}
                                    <option value="__nombre__">Nombre de remitente (alfanumérico)…</option>
                                </select>
                                {modoPersonalizado && (
                                    <div className="mt-3">
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="text"
                                                maxLength={11}
                                                value={valorSms}
                                                onChange={e => setFlujoAlerta({ ...flujoAlerta, emisorSms: e.target.value.slice(0, 11) })}
                                                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 dark:text-gray-100 font-mono font-bold w-48"
                                                placeholder="Ej: WarnGen"
                                            />
                                            <span className="text-xs text-amber-700 dark:text-amber-300 font-mono">{valorSms.length}/11</span>
                                        </div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                            En España solo se muestra si el nombre está registrado (CNMC). Si el operador lo rechaza, se envía automáticamente desde el número del cliente.
                                        </p>
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {/* Selector de remitente WhatsApp */}
                    {flujoAlerta.canales.whatsapp && (
                        <div className="mb-6 p-4 border-2 border-dashed border-green-200 dark:border-green-800 rounded-lg bg-green-50 dark:bg-green-900/20">
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                Remitente de WhatsApp
                            </label>
                            <select
                                value={flujoAlerta.emisorWhatsapp || ''}
                                onChange={e => setFlujoAlerta({ ...flujoAlerta, emisorWhatsapp: e.target.value })}
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 dark:text-gray-100 font-mono"
                            >
                                <option value="">Configuración del cliente ({waClienteDefecto})</option>
                                {remitentes.numeros.map(n => (
                                    <option key={n.numero} value={n.numero}>{n.numero} — {n.nombre}</option>
                                ))}
                            </select>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                El número debe estar dado de alta como WhatsApp Sender en Twilio.
                            </p>
                        </div>
                    )}


                    {canalesActivos.map(canal => {
                        const plantillasCanal = plantillas.filter(p => p.tipo === canal && p.tipo_alerta_codigo === flujoAlerta.tipoAlerta && p.activa !== false);
                        return (
                            <div key={canal} className="mb-6">
                                <h4 className="font-semibold text-gray-700 mb-3 capitalize">Plantilla para {canal}</h4>
                                {plantillasCanal.length === 0 ? (
                                    <p className="text-sm text-gray-500 italic">No hay plantillas activas</p>
                                ) : (
                                    <div className="space-y-2">
                                        {plantillasCanal.map(plantilla => (
                                            <label key={plantilla.id} className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition ${flujoAlerta.plantillasSeleccionadas[canal] === plantilla.id ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/30' : 'border-gray-300 dark:border-gray-600 hover:border-primary-400'}`}>
                                                <input
                                                    type="radio"
                                                    name={`plantilla-${canal}`}
                                                    checked={flujoAlerta.plantillasSeleccionadas[canal] === plantilla.id}
                                                    onChange={() => setFlujoAlerta({
                                                        ...flujoAlerta,
                                                        plantillasSeleccionadas: { ...flujoAlerta.plantillasSeleccionadas, [canal]: plantilla.id }
                                                    })}
                                                    className="mt-1"
                                                />
                                                <div className="flex-1">
                                                    <div className="font-semibold text-gray-800 dark:text-gray-100">{plantilla.nombre}</div>
                                                    {plantilla.asunto && <div className="text-sm text-gray-600 dark:text-gray-400 mt-1"><strong>Asunto:</strong> {plantilla.asunto}</div>}
                                                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-2 bg-gray-50 dark:bg-gray-700 p-2 rounded border dark:border-gray-600">
                                                        <pre className="whitespace-pre-wrap font-mono text-xs">{plantilla.mensaje.substring(0, 150)}...</pre>
                                                    </div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    <div className="mt-6 flex gap-3">
                        <button onClick={() => setFlujoAlerta({ ...flujoAlerta, paso: 2 })} className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 transition font-semibold">Atrás</button>
                        <button
                            onClick={() => {
                                if (!canalesActivos.every(c => flujoAlerta.plantillasSeleccionadas[c])) return alert('Selecciona una plantilla para cada canal');
                                setFlujoAlerta({ ...flujoAlerta, paso: 4 });
                            }}
                            className="flex-1 bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition font-semibold flex items-center justify-center gap-2"
                        >
                            Continuar <ChevronRight size={20} />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Paso 4: Contactos
    if (flujoAlerta.paso === 4) {
        const contactosActivos = contactos.filter(c => c.activo);

        const toggleSelection = (ids, select) => {
            const current = new Set(flujoAlerta.contactosSeleccionados);
            ids.forEach(id => select ? current.add(id) : current.delete(id));
            setFlujoAlerta({ ...flujoAlerta, contactosSeleccionados: Array.from(current) });
        };

        const selectBy = (field, value) => {
            const ids = contactosActivos.filter(c => c[field] === value).map(c => c.id);
            const allSelected = ids.every(id => flujoAlerta.contactosSeleccionados.includes(id));
            toggleSelection(ids, !allSelected);
        };

        // Cargar contactos de una lista y SUMARLOS a la selección actual
        const aplicarLista = async (lista) => {
            if (!lista || !fetchContactosDeLista) return;
            if (listasAplicadas.find(l => l.grupo_id === lista.grupo_id)) return; // ya aplicada
            setCargandoLista(true);
            try {
                const data = await fetchContactosDeLista(lista.grupo_id, 1, 2000);
                if (data.success && data.contactos?.length > 0) {
                    const ids = data.contactos.map(c => c.id);
                    const current = new Set(flujoAlerta.contactosSeleccionados);
                    ids.forEach(id => current.add(id));
                    setFlujoAlerta({ ...flujoAlerta, contactosSeleccionados: Array.from(current) });
                    setListasAplicadas(prev => [...prev, { ...lista, total: data.contactos.length }]);
                }
            } catch (e) { console.error('Error aplicando lista:', e); }
            finally { setCargandoLista(false); }
        };

        const quitarLista = async (lista) => {
            // Quitar los IDs de esa lista que no estén en otras listas aplicadas
            if (!fetchContactosDeLista) return;
            setCargandoLista(true);
            try {
                const data = await fetchContactosDeLista(lista.grupo_id, 1, 2000);
                if (data.success) {
                    const idsAQuitar = new Set(data.contactos.map(c => c.id));
                    // Mantener los que pertenecen a otras listas aplicadas
                    const otraslistas = listasAplicadas.filter(l => l.grupo_id !== lista.grupo_id);
                    for (const otraLista of otraslistas) {
                        const otraData = await fetchContactosDeLista(otraLista.grupo_id, 1, 2000);
                        if (otraData.success) otraData.contactos.forEach(c => idsAQuitar.delete(c.id));
                    }
                    const current = new Set(flujoAlerta.contactosSeleccionados);
                    idsAQuitar.forEach(id => current.delete(id));
                    setFlujoAlerta({ ...flujoAlerta, contactosSeleccionados: Array.from(current) });
                }
            } catch (e) { console.error('Error quitando lista:', e); }
            finally {
                setCargandoLista(false);
                setListasAplicadas(prev => prev.filter(l => l.grupo_id !== lista.grupo_id));
            }
        };

        const listasCompletadas = listas;

        return (
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <button onClick={() => setFlujoAlerta({ ...flujoAlerta, paso: 3 })} className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">
                        <ChevronLeft size={24} />
                    </button>
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Crear Alerta {flujoAlerta.tipoAlerta}</h2>
                </div>
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Paso 4 de 6: Selecciona los destinatarios</h3>

                    <AdvancedContactSelector 
                        contactosActivos={contactosActivos}
                        listasCompletadas={listasCompletadas}
                        fetchContactosDeLista={fetchContactosDeLista}
                        contactosSeleccionados={flujoAlerta.contactosSeleccionados}
                        setContactosSeleccionados={(ids) => setFlujoAlerta({ ...flujoAlerta, contactosSeleccionados: ids })}
                    />

                    <div className="mt-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                            Total seleccionado para el envío: <span className="text-primary-600 dark:text-primary-400 text-lg">{flujoAlerta.contactosSeleccionados.length}</span> destinatarios
                        </span>
                        <button
                            onClick={() => setFlujoAlerta({ ...flujoAlerta, contactosSeleccionados: [] })}
                            className="text-gray-500 hover:text-red-500 text-sm font-semibold hover:underline"
                            disabled={flujoAlerta.contactosSeleccionados.length === 0}
                        >
                            Vaciar selección
                        </button>
                    </div>

                    <div className="mt-6 flex gap-3">
                        <button onClick={() => setFlujoAlerta({ ...flujoAlerta, paso: 3 })} className="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-100 px-6 py-3 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition font-semibold">Atrás</button>
                        <button
                            onClick={() => {
                                if (flujoAlerta.contactosSeleccionados.length === 0) return alert('Selecciona al menos un contacto');
                                setFlujoAlerta({ ...flujoAlerta, paso: 5 });
                            }}
                            className="flex-1 bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition font-semibold flex items-center justify-center gap-2"
                        >
                            Continuar <ChevronRight size={20} />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Paso 5: Programación
    if (flujoAlerta.paso === 5) {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <button onClick={() => setFlujoAlerta({ ...flujoAlerta, paso: 4 })} className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"><ChevronLeft size={24} /></button>
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Crear Alerta {flujoAlerta.tipoAlerta}</h2>
                </div>
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Paso 5 de 6: Programación de envío</h3>
                    <div className="space-y-4">
                        <label className="flex items-center gap-3 p-4 border dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                            <input
                                type="checkbox"
                                checked={flujoAlerta.programacion.inmediata}
                                onChange={(e) => setFlujoAlerta({ ...flujoAlerta, programacion: { ...flujoAlerta.programacion, inmediata: e.target.checked } })}
                                className="w-5 h-5"
                            />
                            <div>
                                <div className="font-semibold dark:text-gray-100">Envío inmediato</div>
                                <div className="text-sm text-gray-600 dark:text-gray-400">Enviar ahora mismo</div>
                            </div>
                        </label>

                        <label className="flex items-center gap-3 p-4 border dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                            <input
                                type="checkbox"
                                checked={flujoAlerta.programacion.programada.activa}
                                onChange={(e) => setFlujoAlerta({ ...flujoAlerta, programacion: { ...flujoAlerta.programacion, programada: { ...flujoAlerta.programacion.programada, activa: e.target.checked } } })}
                                className="w-5 h-5"
                            />
                            <div>
                                <div className="font-semibold dark:text-gray-100">Programar envío</div>
                                <div className="text-sm text-gray-600 dark:text-gray-400">Enviar en una fecha específica</div>
                            </div>
                        </label>
                        {flujoAlerta.programacion.programada.activa && (
                            <div className="ml-0 sm:ml-8 p-4 bg-gray-50 dark:bg-gray-700 rounded border dark:border-gray-600" style={{position: 'relative', zIndex: 1000}}>
                                <label className="block text-sm font-medium dark:text-gray-300 mb-1">Fecha y hora:</label>
                                <input
                                    type="datetime-local"
                                    value={flujoAlerta.programacion.programada.fechaHora}
                                    onChange={(e) => setFlujoAlerta({ ...flujoAlerta, programacion: { ...flujoAlerta.programacion, programada: { ...flujoAlerta.programacion.programada, fechaHora: e.target.value } } })}
                                    className="w-full p-2 border dark:border-gray-500 rounded bg-white dark:bg-gray-600 dark:text-gray-100"
                                    style={{position: 'relative', zIndex: 9999}}
                                />
                            </div>
                        )}
                    </div>
                    <div className="mt-6 flex flex-col-reverse sm:flex-row gap-3">
                        <button onClick={() => setFlujoAlerta({ ...flujoAlerta, paso: 4 })} className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 transition font-semibold">Atrás</button>
                        <button onClick={generarPreview} className="flex-1 bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition font-semibold flex items-center justify-center gap-2">Continuar a Vista Previa</button>
                    </div>
                </div>
            </div>
        );
    }

    // Paso 6: Preview (Sustituye al antiguo paso 7)
    if (flujoAlerta.paso === 6) {
        const canalesActivos = Object.keys(flujoAlerta.canales).filter(c => flujoAlerta.canales[c]);
        
        // Obtener variables necesarias escaneando tanto los metadatos como el texto del mensaje
        const varsNecesarias = new Set();
        canalesActivos.forEach(canal => {
            const plantillaId = flujoAlerta.plantillasSeleccionadas[canal];
            const plantilla = plantillas.find(p => p.id === plantillaId);
            if (plantilla) {
                // 1. Añadir variables desde metadatos
                const varsManuales = Array.isArray(plantilla.variables) ? plantilla.variables : JSON.parse(plantilla.variables || '[]');
                varsManuales.forEach(v => varsNecesarias.add(v.toString()));

                // 2. Escanear texto en busca de {{variable}} o {{num}}
                const matches = (plantilla.mensaje + (plantilla.asunto || '')).matchAll(/{{(.*?)}}/g);
                for (const match of matches) {
                    varsNecesarias.add(match[1].trim());
                }
            }
        });

        const renderTextoConResaltado = (texto) => {
            if (!texto) return null;
            const partes = texto.split(/(\[\[RESALTAR\].*?\[\[FIN\]\])/g);
            return partes.map((parte, i) => {
                if (parte.startsWith('[[RESALTAR]]') && parte.endsWith('[[FIN]]')) {
                    const contenido = parte.replace('[[RESALTAR]]', '').replace('[[FIN]]', '');
                    return (
                        <span key={i} className="bg-blue-100 text-blue-800 px-1 rounded border border-blue-200 font-bold animate-pulse">
                            {contenido}
                        </span>
                    );
                }
                return parte;
            });
        };

        const handleVarChange = (varName, value) => {
            const nuevasVars = { ...flujoAlerta.variables, [varName]: value };
            const primerContacto = contactos.find(c => flujoAlerta.contactosSeleccionados.includes(c.id)) || contactos[0] || null;
            const autoValoresContacto = {
                nombre: primerContacto?.nombre || 'María García',
                zona: primerContacto?.zona || primerContacto?.distrito_municipal || 'Distrito 1 — Centro',
                distrito: primerContacto?.zona || primerContacto?.distrito_municipal || 'Distrito 1 — Centro',
                distrito_municipal: primerContacto?.zona || primerContacto?.distrito_municipal || 'Distrito 1 — Centro',
            };
            
            // Actualizar preview en tiempo real
            const nuevosPreviews = {};
            canalesActivos.forEach(canal => {
                const plantilla = plantillas.find(p => p.id === flujoAlerta.plantillasSeleccionadas[canal]);
                if (plantilla) {
                    let mensaje = plantilla.mensaje;
                    let asunto = plantilla.asunto || '';
                    const varsManuales = Array.isArray(plantilla.variables) ? plantilla.variables : JSON.parse(plantilla.variables || '[]');
                    const detectadas = [...(mensaje + asunto).matchAll(/{{(.*?)}}/g)].map(m => m[1].trim());
                    const todasVars = [...new Set([...varsManuales.map(v => v.toString().trim()), ...detectadas, ...Object.keys(nuevasVars)])];
                    
                    todasVars.forEach(vn => {
                        const vnClean = vn.toString().trim();
                        let val = nuevasVars[vnClean];
                        if (!val && !isNaN(vnClean)) {
                            const mappedName = varsManuales[Number(vnClean) - 1];
                            if (mappedName) {
                                val = nuevasVars[mappedName] || autoValoresContacto[mappedName];
                            }
                        }
                        if (!val && autoValoresContacto[vnClean]) {
                            val = autoValoresContacto[vnClean];
                        }
                        const valMarker = val ? `[[RESALTAR]]${val}[[FIN]]` : `{{${vnClean}}}`;
                        
                        // Reemplazo Directo (Soporta espacios opcionales ej: {{ var }} o {{var}})
                        const regexDirect = new RegExp(`{{\\s*${vnClean}\\s*}}`, 'g');
                        mensaje = mensaje.replace(regexDirect, valMarker);
                        asunto = asunto.replace(regexDirect, valMarker);

                        // Soporte Especial WhatsApp (nombre -> {{indice}})
                        if (canal === 'whatsapp') {
                            const idx = varsManuales.findIndex(v => v.toString().trim() === vnClean);
                            if (idx !== -1) {
                                const regexIdx = new RegExp(`{{\\s*${idx + 1}\\s*}}`, 'g');
                                mensaje = mensaje.replace(regexIdx, valMarker);
                            }
                        }
                    });

                    nuevosPreviews[canal] = { asunto, mensaje };
                }
            });

            setFlujoAlerta({ ...flujoAlerta, variables: nuevasVars, preview: nuevosPreviews });
        };

        return (
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <button onClick={() => setFlujoAlerta({ ...flujoAlerta, paso: 5 })} className="text-gray-600 hover:text-gray-800"><ChevronLeft size={24} /></button>
                    <h2 className="text-2xl font-bold text-gray-800">Crear Alerta {flujoAlerta.tipoAlerta}</h2>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Paso 6 de 6: Vista Previa y Datos Dinámicos</h3>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="md:col-span-1 space-y-4">
                            <div className="bg-gray-50 p-4 rounded-lg border">
                                <h4 className="font-bold text-sm text-gray-700 uppercase mb-3 text-center border-b pb-2">Datos Dinámicos</h4>
                                {canalesActivos.map(canal => {
                                    const plantillaId = flujoAlerta.plantillasSeleccionadas[canal];
                                    const plantilla = plantillas.find(p => p.id === plantillaId);
                                    if (!plantilla) return null;

                                    const varsManuales = Array.isArray(plantilla.variables) ? plantilla.variables : JSON.parse(plantilla.variables || '[]');
                                    const detectadas = [...(plantilla.mensaje + (plantilla.asunto || '')).matchAll(/{{(.*?)}}/g)]
                                        .map(m => m[1].trim())
                                        .filter(d => !(canal === 'whatsapp' && !isNaN(d) && varsManuales[Number(d) - 1]));
                                    const varsDeCanal = [...new Set([...varsManuales.map(v => v.toString().trim()), ...detectadas])];

                                    if (varsDeCanal.length === 0) return null;

                                    return (
                                        <div key={canal} className="mb-6 p-3 bg-white rounded border border-gray-200 shadow-sm transition-all hover:border-primary-300">
                                            <h5 className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase mb-3 tracking-widest border-b pb-1">
                                                {canal === 'email' && <Mail size={12} />}
                                                {canal === 'sms' && <Phone size={12} />}
                                                {canal === 'whatsapp' && <MessageSquare size={12} />}
                                                Variables {canal}
                                            </h5>
                                            <div className="space-y-3">
                                                {varsDeCanal.map(v => {
                                                    const idx = canal === 'whatsapp' ? varsManuales.findIndex(vm => vm.toString().trim() === v.toString().trim()) : -1;
                                                    const esAutoContacto = ['nombre', 'zona', 'distrito', 'distrito_municipal'].includes(v.toLowerCase());
                                                    return (
                                                        <div key={v}>
                                                            <div className="flex items-center justify-between gap-1 mb-1">
                                                                <label className="block text-[10px] font-bold text-primary-600 uppercase bg-primary-50 px-1 inline-block rounded">
                                                                    {v} {idx !== -1 && <span className="text-[9px] opacity-60">(&#123;&#123;{idx + 1}&#125;&#125;)</span>}
                                                                </label>
                                                                {esAutoContacto && (
                                                                    <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded font-medium">
                                                                        Automático por contacto
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <input
                                                                type="text"
                                                                value={flujoAlerta.variables[v] || ''}
                                                                onChange={(e) => handleVarChange(v, e.target.value)}
                                                                className="w-full px-2 py-1.5 border border-gray-200 rounded focus:ring-1 focus:ring-primary-500 text-xs outline-none bg-gray-50 focus:bg-white transition-colors"
                                                                placeholder={esAutoContacto ? `Automático (se usa ${v} de cada contacto)` : `Valor para {{${v}}}`}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}

                                {canalesActivos.every(canal => {
                                    const p = plantillas.find(p => p.id === flujoAlerta.plantillasSeleccionadas[canal]);
                                    if (!p) return true;
                                    const vm = Array.isArray(p.variables) ? p.variables : JSON.parse(p.variables || '[]');
                                    const dt = [...(p.mensaje + (p.asunto || '')).matchAll(/{{(.*?)}}/g)];
                                    return vm.length === 0 && dt.length === 0;
                                }) && (
                                    <p className="text-xs text-gray-500 italic text-center py-4 bg-gray-50 rounded border border-dashed">
                                        No se han detectado variables en las plantillas seleccionadas.
                                    </p>
                                )}
                            </div>

                            <div className="bg-primary-50 p-4 rounded-lg border border-primary-100 italic">
                                <h4 className="font-semibold text-sm mb-1 text-primary-800 underline">Resumen de Envío</h4>
                                <div className="text-xs space-y-1 text-primary-700">
                                    <p>Canales: {canalesActivos.join(', ')}</p>
                                    <p>Destinatarios: {flujoAlerta.contactosSeleccionados.length}</p>
                                    <p>Programación: {flujoAlerta.programacion.inmediata ? 'Inmediata' : 'Fecha específica'}</p>
                                </div>
                            </div>
                        </div>

                        <div className="md:col-span-2 space-y-4">
                            {canalesActivos.map(canal => (
                                <div key={canal} className="border-2 border-gray-200 rounded-lg overflow-hidden relative shadow-sm">
                                    <div className="bg-gray-100 px-4 py-2 border-b flex items-center justify-between">
                                        <h5 className="font-bold text-xs uppercase text-gray-600 flex items-center gap-2">
                                            {canal === 'email' && <Mail size={14} />}
                                            {canal === 'sms' && <Phone size={14} />}
                                            {canal === 'whatsapp' && <MessageSquare size={14} />}
                                            VISTA PREVIA {canal}
                                        </h5>
                                        <span className="text-[10px] text-gray-400">Mensaje Final</span>
                                    </div>
                                    {canal === 'email' ? (
                                        <div className="p-3 bg-gray-50">
                                            <EmailPreview
                                                mensaje={flujoAlerta.preview[canal]?.mensaje || ''}
                                                asunto={
                                                    (flujoAlerta.preview[canal]?.asunto || '')
                                                        .replace(/\[\[RESALTAR\]\](.*?)\[\[FIN\]\]/g, '$1')
                                                }
                                                altura="h-[460px]"
                                                mostrarToggle={true}
                                            />
                                        </div>
                                    ) : (
                                        <div className="p-4 bg-white min-h-[100px] max-h-[300px] overflow-y-auto">
                                            <div className="text-sm text-gray-800 whitespace-pre-wrap font-sans leading-relaxed">
                                                {renderTextoConResaltado(flujoAlerta.preview[canal]?.mensaje) || <span className="text-gray-300 italic">Previsualización del mensaje...</span>}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mt-8 flex flex-col-reverse sm:flex-row gap-3 pt-6 border-t">
                        <button onClick={() => setFlujoAlerta({ ...flujoAlerta, paso: 5 })} className="w-full sm:w-auto px-8 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition font-semibold">Atrás</button>
                        <button 
                            onClick={enviarAlertas}
                            className="flex-1 bg-green-600 text-white px-8 py-3.5 rounded-lg hover:bg-green-700 transition font-bold flex items-center justify-center gap-2 shadow-lg hover:-translate-y-0.5"
                        >
                            <CheckCircle size={24} />
                            ENVIAR ALERTA
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return null;
};

export default CreateAlert;
